"use client";

import { Suspense, useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import CouponCard from "./CouponCard";
import CouponModal from "./CouponModal";
import NewsletterSignup from "./NewsletterSignup";
import type { CardHistory } from "@/lib/client-props";
import type { OfferFacts } from "@/lib/offer-facts";
import "./store/store-v5.css";

interface Coupon {
    id: string;
    title: string;
    /** Never sent by the store page (the code is not in the page data); kept for callers that have one. */
    code?: string | null;
    description?: string | null;
    expiry?: string | null;
    verified?: boolean;
    type: string;
    successRate?: number;
    usedToday?: number;
    isFavorite?: boolean;
    isExclusive?: boolean;
    discountValue?: number | null;
    discountType?: "PERCENT" | "FIXED" | "FREE_SHIPPING" | "DEAL" | null;
    /** ISO time of a failed latest check (SEO R3 rule 6), or null. */
    lastCheckFailedAt?: string | null;
    /** ISO time of the newest VALID run of a failed-check code ("Last worked {date}"), or null. */
    lastWorkedAt?: string | null;
    /** Offer facts row (lib/offer-facts), shown under the offer text. */
    facts?: OfferFacts | null;
    store?: {
        name: string;
        logo: string;
        affiliateLink: string;
        slug: string;
        id: string;
    };
}

/**
 * Reads `?couponId=` on the client so the store page itself can stay static (ISR).
 * Renders nothing; reports the id upward. It lives in its own Suspense boundary so
 * the rest of the page is prerendered. (The `#couponId-` hash, which the "Get Code"
 * buttons use, is handled separately in CouponGrid and takes precedence.)
 */
function CouponIdFromQuery({ onFound }: { onFound: (id: string) => void }) {
    const id = useSearchParams().get("couponId");
    useEffect(() => {
        if (id) onFound(id);
    }, [id, onFound]);
    return null;
}

interface CouponGridProps {
    coupons: Coupon[];
    expiredCoupons?: Coupon[];
    affiliateLink: string;
    totalOffers: number;
    storeSlug: string;
    storeName: string;
    storeId: string;
    /** Unused since the list heading has no date (SEO R3 rule 3); kept so callers need no change. */
    formattedDate?: string;
    /** v2 stores: the whole active-list heading ("Active {Brand} promo codes", no date). Not used for the Deals filter. */
    listHeading?: string;
    initialCouponId?: string;
    curatorName?: string;
    hideHeader?: boolean;
    /** Signed reveal token for this store (rendered server-side), passed to the modal. */
    revealToken?: string;
    /** Public verification history per coupon id (server-built from real runs and votes). */
    histories?: Record<string, CardHistory>;
    /** ISO time the page data was produced. */
    /** The page this grid sits on (default `/{storeSlug}/coupons`); a REFERRAL page passes `/{slug}/referral-code`. */
    pagePath?: string;
    /** Page type: only the modal's wording differs on a REFERRAL page. */
    pageType?: "COUPON" | "REFERRAL";
}

export default function CouponGrid({ coupons, expiredCoupons = [], affiliateLink, totalOffers, storeSlug, storeName, storeId, initialCouponId, listHeading, curatorName, hideHeader = false, revealToken, histories = {}, pagePath: pagePathProp, pageType }: CouponGridProps) {
    const pagePath = pagePathProp ?? `/${storeSlug}/coupons`;
    const [selectedType, setSelectedType] = useState<"ALL" | "CODE" | "DEAL">("ALL");
    const [selectedCouponId, setSelectedCouponId] = useState<string | null>(initialCouponId || null);
    // Session and favorites are read on the client: the page HTML is cached and identical for everyone.
    const { data: session } = useSession();
    const userId = session?.user?.id;
    const [favoriteIds, setFavoriteIds] = useState<Set<string>>(() => new Set());
    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        fetch("/api/favorites")
            .then((res) => res.json())
            .then((data) => { if (!cancelled && Array.isArray(data.ids)) setFavoriteIds(new Set<string>(data.ids)); })
            .catch(() => { });
        return () => { cancelled = true; };
    }, [userId]);
    // `?couponId=` (read via useSearchParams); falls back to the prop for callers that still pass it.
    const [queryCouponId, setQueryCouponId] = useState<string | undefined>(initialCouponId);

    // Auto-open modal from props OR from URL hash fragment
    useEffect(() => {
        // First check hash fragment (client-side)
        const hash = window.location.hash;
        if (hash.startsWith('#couponId-')) {
            const hashCouponId = hash.replace('#couponId-', '');
            if (hashCouponId) {
                setSelectedCouponId(hashCouponId);
                return;
            }
        }
        // Fallback to the ?couponId= query param
        if (queryCouponId) {
            setSelectedCouponId(queryCouponId);
        }
    }, [queryCouponId]);

    // Listen for hash changes (browser back/forward)
    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash;
            if (hash.startsWith('#couponId-')) {
                const hashCouponId = hash.replace('#couponId-', '');
                setSelectedCouponId(hashCouponId || null);
            } else if (!hash) {
                setSelectedCouponId(null);
            }
        };
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    const handleOpenCoupon = (id: string) => {
        setSelectedCouponId(id);
        // Optional: Update URL without reload if needed, or rely on Link
        window.history.pushState({}, "", `${pagePath}#couponId-${id}`);
    };

    const handleCloseModal = () => {
        setSelectedCouponId(null);
        window.history.pushState({}, "", pagePath);
    };

    const filteredCoupons = coupons.filter((coupon) => {
        if (selectedType === "ALL") return true;
        return coupon.type === selectedType;
    });

    // Search in both active and expired coupons for modal
    const allCouponsForModal = [...coupons, ...expiredCoupons];
    const activeCoupon = allCouponsForModal.find(c => c.id === selectedCouponId);

    // No live offer (audit 2026-10-25 item 9): no filter tabs, no empty "Active ... codes" heading and no "No coupons found" line. The
    // page writes one sentence and lists the expired offers first (StorePage); this component stays only as the host of the offer
    // modal, so a `#couponId-` link to an expired offer still opens it.
    const noLiveOffer = coupons.length === 0;

    return (
        <div className="sp-scope space-y-4">
            <Suspense fallback={null}>
                <CouponIdFromQuery onFound={setQueryCouponId} />
            </Suspense>

            {/* Modal */}
            {activeCoupon && (
                <CouponModal
                    isOpen={!!activeCoupon}
                    onClose={handleCloseModal}
                    session={session}
                    revealToken={revealToken}
                    pageType={pageType}
                    coupon={{
                        id: activeCoupon.id,
                        title: activeCoupon.title,
                        code: activeCoupon.code || "",
                        description: activeCoupon.description || "",
                        isFavorite: favoriteIds.has(activeCoupon.id),
                        type: activeCoupon.type,
                        affiliateLink: activeCoupon.store?.affiliateLink
                    }}
                    store={activeCoupon.store ? {
                        name: activeCoupon.store.name,
                        logo: activeCoupon.store.logo,
                        affiliateLink: activeCoupon.store.affiliateLink
                    } : {
                        name: storeName,
                        logo: "",
                        affiliateLink: `/out/${storeId}`
                    }}
                />
            )}

            {!noLiveOffer && (<>
            {/* Filter Tabs */}
            <div className="sp-filters" role="group" aria-label="Filter offers">
                {(["ALL", "CODE", "DEAL"] as const).map((type) => (
                    <button
                        key={type}
                        type="button"
                        onClick={() => setSelectedType(type)}
                        aria-pressed={selectedType === type}
                        className="sp-filter"
                    >
                        {type === "ALL" ? `All Offers (${totalOffers})` : type === "CODE" ? `Codes (${coupons.filter(c => c.type.toUpperCase() === "CODE").length})` : `Deals (${coupons.filter(c => c.type.toUpperCase() === "DEAL").length})`}
                    </button>
                ))}
            </div>

            {!hideHeader && (
                <h2 className="text-2xl font-bold text-white mb-6 tracking-tight">
                    {/* No date in the list heading (SEO R3 rule 3, 2026-10-24). */}
                    {listHeading && selectedType !== 'DEAL'
                        ? listHeading
                        : `Active ${storeName} ${selectedType === 'DEAL' ? 'deals' : 'coupon codes'}`}
                </h2>
            )}

            {/* Coupon List */}
            <div className="sp-coupon-stack">
                {filteredCoupons.length > 0 ? (
                    <>
                        {filteredCoupons.slice(0, 2).map((coupon) => (
                            <CouponCard
                                key={coupon.id}
                                id={coupon.id}
                                title={coupon.title}
                                code={coupon.code || ""}
                                description={coupon.description || ""}
                                expiry={coupon.expiry || undefined}
                                verified={coupon.verified}
                                successRate={coupon.successRate}
                                usedToday={coupon.usedToday}
                                affiliateLink={affiliateLink}
                                storeSlug={storeSlug}
                                storeName={storeName}
                                storeId={storeId}
                                onOpen={() => handleOpenCoupon(coupon.id)}
                                pagePath={pagePath}
                                type={coupon.type}
                                isFavorite={favoriteIds.has(coupon.id)}
                                curatorName={curatorName}
                                isExclusive={coupon.isExclusive}
                                discountValue={coupon.discountValue}
                                discountType={coupon.discountType as any}
                                history={histories[coupon.id]}
                                lastCheckFailedAt={coupon.lastCheckFailedAt}
                                lastWorkedAt={coupon.lastWorkedAt}
                                facts={coupon.facts}
                            />
                        ))}

                        {/* Newsletter Signup Injection */}
                        <NewsletterSignup storeName={storeName} />

                        {filteredCoupons.slice(2).map((coupon) => (
                            <CouponCard
                                key={coupon.id}
                                id={coupon.id}
                                title={coupon.title}
                                code={coupon.code || ""}
                                affiliateLink={affiliateLink}
                                storeSlug={storeSlug}
                                storeName={storeName}
                                storeId={storeId}
                                description={coupon.description || ""}
                                expiry={coupon.expiry || undefined}
                                verified={coupon.verified}
                                successRate={coupon.successRate}
                                usedToday={coupon.usedToday}
                                onOpen={() => handleOpenCoupon(coupon.id)}
                                pagePath={pagePath}
                                type={coupon.type}
                                isExclusive={coupon.isExclusive}
                                discountValue={coupon.discountValue}
                                discountType={coupon.discountType as any}
                                history={histories[coupon.id]}
                                lastCheckFailedAt={coupon.lastCheckFailedAt}
                                lastWorkedAt={coupon.lastWorkedAt}
                                facts={coupon.facts}
                            />
                        ))}
                    </>
                ) : (
                    <div className="sp-empty">
                        {/* One string: "No coupon{s}" as two JSX text nodes read as "No coupon s found" in text extracts. */}
                        {`No ${selectedType === 'ALL' ? 'coupons' : selectedType === 'CODE' ? 'codes' : 'deals'} found for this store.`}
                    </div>
                )}
            </div>
            </>)}
        </div>
    );
}
